import { Button, DialogActions, DialogContent } from "@mui/material";
import { PropsWithChildren, ReactElement } from "react";
import {
  Control,
  Path,
  UseFormGetValues,
  FieldValues,
  UseFormWatch,
} from "react-hook-form";

import { BaseFieldInput, MetadataFieldTypes } from "@stw/yup-validators";

import {
  MetadataBoolFieldFragment,
  MetadataEnumFieldFragment,
  MetadataEnumMultiFieldFragment,
  MetadataStringFieldFragment,
  MetadataUserListFieldFragment,
  MetadataStateFieldFragment,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import { SelectFieldType } from "~frontend/components/campaignMetadata/common";
import {
  RhfSwitchField,
  RhfTextField,
} from "~frontend/components/forms/react-hook-form-components";

type FieldFormProps<T extends BaseFieldInput & FieldValues> = {
  editField?:
    | MetadataStringFieldFragment
    | MetadataBoolFieldFragment
    | MetadataEnumFieldFragment
    | MetadataEnumMultiFieldFragment
    | MetadataUserListFieldFragment
    | MetadataStateFieldFragment;
  onTypeChange: (name: string, type: MetadataFieldTypes) => void;
  currentFieldType: MetadataFieldTypes;
  onCancelClick: () => void;
  getValues: UseFormGetValues<T>;
  isSubmitting: boolean;
  handleSubmit: () => void;
  control: Control<T, unknown, T>;
  watch: UseFormWatch<T>;
};

export function FieldForm<T extends BaseFieldInput>({
  editField,
  onTypeChange,
  getValues,
  onCancelClick,
  children,
  isSubmitting,
  handleSubmit,
  control,
  currentFieldType,
  watch,
}: PropsWithChildren<FieldFormProps<T>>): ReactElement {
  const isFieldRequired = watch("required" as Path<T>);
  const requiredTxt = isFieldRequired ? "required" : "not required";
  const requiredToggleLabel = `This field is ${requiredTxt}`;

  return (
    <form onSubmit={handleSubmit}>
      <DialogContent sx={{ pt: 0, pb: 0 }}>
        <FlexBox direction="column">
          <RhfTextField
            control={control}
            label="Metadata Field Name"
            name={"name" as Path<T>}
          />
          <SelectFieldType
            disabled={Boolean(editField)}
            value={currentFieldType}
            onChange={newType => {
              const name = getValues("name" as Path<T>);
              onTypeChange(name as string, newType);
            }}
          />
        </FlexBox>
        {children}
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: "space-between",
          alignItems: "center",
          pt: 0,
          margin: 2,
        }}
      >
        <RhfSwitchField
          label={requiredToggleLabel}
          name={"required" as Path<T>}
          control={control}
        />
        <FlexBox gap={1}>
          <Button
            onClick={onCancelClick}
            color="warning"
            variant="contained"
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            color="primary"
            variant="contained"
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            {editField ? "SAVE" : "CREATE METADATA FIELD "}
          </Button>
        </FlexBox>
      </DialogActions>
    </form>
  );
}
